import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { api } from "../lib/api";
import {
  Sparkles,
  Wand2,
  LayoutDashboard,
  Loader2,
  Eye,
  Rocket,
  AlertCircle,
  CheckCircle2
} from "lucide-react";

const EXAMPLES = [
  "Create an inventory dashboard with total stock, stock by warehouse, low stock items, and stock movement trend.",
  "Create a sales dashboard with total sales, sales trend, top customers, and detailed sales table.",
  "Create a shipment dashboard with total shipments, shipment status, customer-wise shipment, and recent shipment details.",
  "Create a purchase dashboard with total purchase quantity, purchase trend, supplier-wise purchase, and pending purchase lines.",
];

export default function AIAnalyticsAgent() {
  const nav = useNavigate();
  const [prompt, setPrompt] = useState(EXAMPLES[0]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [creating, setCreating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [createdDashboard, setCreatedDashboard] = useState(null);
  const [error, setError] = useState("");

  const generatePlan = async () => {
    const text = prompt.trim();
    if (text.length < 8) {
      setError("Please enter a clearer analytics prompt.");
      return;
    }

    setError("");
    setCreatedDashboard(null);
    setLoadingPlan(true);

    try {
      const r = await api.post("/ai/analytics-agent/plan", { prompt: text });
      setPlan(r.data.plan);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to generate AI plan.");
    } finally {
      setLoadingPlan(false);
    }
  };

  const createDashboard = async () => {
    const text = prompt.trim();
    if (text.length < 8) {
      setError("Please enter a clearer analytics prompt.");
      return;
    }

    setError("");
    setCreating(true);

    try {
      const r = await api.post("/ai/analytics-agent/create-dashboard", { prompt: text });
      setCreatedDashboard(r.data.dashboard);
      setPlan(r.data.plan);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to create dashboard.");
    } finally {
      setCreating(false);
    }
  };

  const widgetCount = plan?.widgets?.length || 0;
  const filterCount = plan?.filters?.length || 0;

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 min-w-0">
        <Topbar
          title="AI Analytics Agent"
          subtitle="Create dashboards, reports, and analytics layouts from simple business prompts."
        />

        <div className="p-6 space-y-5">
          <div className="ai-agent-hero">
            <div className="ai-agent-icon">
              <Sparkles size={22} strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <div className="ai-agent-title">Ask Quantira AI to build analytics for you</div>
              <div className="ai-agent-subtitle">
                Describe what you need. The agent will select a suitable dataset, prepare KPIs, charts, filters, and a dashboard layout.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 xl:col-span-5 space-y-4">
              <div className="card-flat p-4">
                <div className="section-title mb-1">Prompt</div>
                <div className="section-sub mb-3">
                  Example: “Create an inventory dashboard with stock trend and warehouse-wise stock.”
                </div>

                <textarea
                  className="input ai-agent-textarea"
                  rows="8"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Tell AI what dashboard or report you want to create..."
                  data-testid="ai-agent-prompt"
                />

                {error && (
                  <div className="mt-3 text-[12.5px] px-3 py-2 rounded flex items-start gap-2"
                    style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}
                    data-testid="ai-agent-error"
                  >
                    <AlertCircle size={14} strokeWidth={1.7} className="mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    className="btn btn-outline"
                    onClick={generatePlan}
                    disabled={loadingPlan || creating}
                    data-testid="ai-agent-generate-plan"
                  >
                    {loadingPlan ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} strokeWidth={1.7} />}
                    Preview plan
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={createDashboard}
                    disabled={loadingPlan || creating}
                    data-testid="ai-agent-create-dashboard"
                  >
                    {creating ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} strokeWidth={1.7} />}
                    Create dashboard
                  </button>
                </div>
              </div>

              <div className="card-flat p-4">
                <div className="section-title mb-2">Quick examples</div>
                <div className="space-y-2">
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      className="ai-example"
                      onClick={() => {
                        setPrompt(ex);
                        setPlan(null);
                        setCreatedDashboard(null);
                        setError("");
                      }}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-12 xl:col-span-7 space-y-4">
              {createdDashboard && (
                <div className="card-flat p-4 ai-created-box" data-testid="ai-agent-created">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={18} strokeWidth={1.8} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[13.5px]">Dashboard created successfully</div>
                      <div className="text-[12px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                        {createdDashboard.name}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => nav(`/dashboards/${createdDashboard.id}`)}
                    >
                      <LayoutDashboard size={14} strokeWidth={1.7} />
                      Open
                    </button>
                  </div>
                </div>
              )}

              <div className="card-flat p-4 min-h-[420px]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="section-title">AI dashboard plan</div>
                    <div className="section-sub">
                      Review what the agent will create before saving it.
                    </div>
                  </div>

                  {plan && (
                    <div className="flex gap-2">
                      <span className="pill info">{widgetCount} widgets</span>
                      <span className="pill">{filterCount} filters</span>
                    </div>
                  )}
                </div>

                {!plan ? (
                  <div className="ai-empty-plan">
                    <Wand2 size={24} strokeWidth={1.6} />
                    <div className="mt-3 font-medium">No plan generated yet</div>
                    <div className="mt-1 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
                      Enter a prompt and click Preview plan or Create dashboard.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="ai-agent-plan">
                    <div className="ai-plan-header">
                      <div>
                        <div className="text-[15px] font-semibold">{plan.dashboard_name}</div>
                        <div className="text-[12.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                          {plan.description}
                        </div>
                      </div>
                      <div className="text-right text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                        <div>Dataset</div>
                        <div className="font-medium" style={{ color: "var(--c-text)" }}>
                          {plan.dataset_name || plan.dataset_id || "Not selected"}
                        </div>
                      </div>
                    </div>

                    {plan.filters?.length > 0 && (
                      <div>
                        <div className="kpi-label mb-2">Filters</div>
                        <div className="flex flex-wrap gap-2">
                          {plan.filters.map((f) => (
                            <span key={f.id || `${f.field}-${f.type}`} className="ai-filter-chip">
                              {f.label || f.field} · {f.type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="kpi-label mb-2">Widgets</div>
                      <div className="ai-widget-list">
                        {(plan.widgets || []).map((w, idx) => (
                          <div key={`${w.title}-${idx}`} className="ai-widget-item">
                            <div className="ai-widget-type">{w.type}</div>
                            <div className="min-w-0">
                              <div className="ai-widget-title">{w.title}</div>
                              <div className="ai-widget-meta">
                                {w.measure && <span>Measure: {w.measure}</span>}
                                {w.dimension && <span>Dimension: {w.dimension}</span>}
                                {w.aggregation && <span>Aggregation: {w.aggregation}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {plan.notes?.length > 0 && (
                      <div className="ai-notes">
                        {plan.notes.map((n, idx) => <div key={idx}>• {n}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
